import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '../components/Icon';
import { useSession } from '../lib/session';
import { initials, tone } from '../lib/format';

const SECTION_ICON: Record<string, IconName> = {
  inicio: 'grid',
  relaciones: 'users',
  ventas: 'file',
  compras: 'wallet',
  operaciones: 'route',
  finanzas: 'gauge',
  documentos: 'folder',
  administracion: 'building',
};

function CompanyPicker() {
  const { me, company, switchCompany } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!company || !me) return null;

  return (
    <div className="company-picker" ref={ref}>
      <button className="company-btn" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
        <span className="company-avatar" style={{ background: tone(company.id) }}>
          {initials(company.tradeName)}
        </span>
        <span className="company-btn__body">
          <span className="company-btn__name">{company.tradeName}</span>
          <span className="company-btn__meta">{company.jurisdiction}</span>
        </span>
        <Icon name="chevron-down" size={14} className="company-btn__chev" />
      </button>

      {open && (
        <div className="company-menu" role="listbox">
          {me.companies.map((c) => (
            <button
              key={c.id}
              className={`company-menu__item${c.id === company.id ? ' company-menu__item--active' : ''}`}
              role="option"
              aria-selected={c.id === company.id}
              onClick={() => {
                switchCompany(c.id);
                setOpen(false);
              }}
            >
              <span className="company-avatar" style={{ background: tone(c.id), width: 26, height: 26, fontSize: 11 }}>
                {initials(c.tradeName)}
              </span>
              <span className="company-btn__body">
                <span className="company-btn__name">{c.tradeName}</span>
                <span className="company-btn__meta">{c.legalName}</span>
              </span>
              {c.id === company.id && <Icon name="check" size={15} className="tone-active" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Rail() {
  const { caps } = useSession();
  const nav = useNavigate();
  const loc = useLocation();

  const isActive = (route: string) =>
    route === '/' ? loc.pathname === '/' : loc.pathname === route || loc.pathname.startsWith(route + '/');

  return (
    <aside className="rail">
      <div className="rail__brand">
        <span className="brandmark">N</span>
        <span className="rail__wordmark">Nexo</span>
      </div>

      <CompanyPicker />

      <nav className="rail__nav" aria-label="Navegación principal">
        {(caps?.navigation ?? []).map((section) => {
          const visible = section.items.filter((i) => i.enabled || i.showLocked);
          if (!visible.length) return null;
          return (
            <div className="nav-section" key={section.id}>
              <div className="nav-section__label">{section.label}</div>
              {visible.map((item) => {
                const active = isActive(item.route);
                const locked = !item.enabled;
                return (
                  <button
                    key={item.id}
                    className={
                      'nav-item' +
                      (active ? ' nav-item--active' : '') +
                      (locked ? ' nav-item--locked' : '')
                    }
                    onClick={() => nav(item.route)}
                    title={locked ? item.lockedReason : undefined}
                  >
                    <Icon name={SECTION_ICON[section.id] ?? 'grid'} size={16} className="nav-item__icon" />
                    <span>{item.label}</span>
                    {locked && <Icon name="lock" size={12} className="nav-item__lock" />}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
